import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { isNull } from 'lodash/fp';
import PropTypes from 'prop-types';
import ItemSelectorDetails from './ItemSelectorDetails';
import ItemSelectorValidationErrors from './ItemSelectorValidationErrors';
import { CopyStylesEmpty, CopyStylesProduct, CopyStylesProductInfo } from '../../../themes/svg/copyStylesIcons';
import LoadingSpinner from '../../../themes/svg/loadingSpinner';
import { ItemSelectorCheckbox, StyledItemSelector, ItemSelectorIcon, ItemLoadingSpinner } from './styles';

const ItemSelector = ({
  itemId,
  addItemToSelection,
  removeItemFromSelection,
  isSelected,
  itemName,
  details,
  validation,
  newWindowLabel,
  validateItems,
  sourceItem,
  isSelectedValid,
}) => {
  const [isValidating, setIsValidating] = useState(false);

  const validatingItems = () => {
    validateItems(sourceItem, itemId).then(() => {
      // If the item is valid, add it to the selection.
      setIsValidating(false);
      if (validation.isValid) {
        addItemToSelection(itemId);
      } else {
        // If the item is not valid, log the errors and do not add it to the selection.
        console.log(`Item ${itemId} is not valid and cannot be added to selection. ${validation.errors.join(', ')}`);
        removeItemFromSelection(itemId);
      }
    });
  };

  useEffect(() => {
    if (!isSelectedValid) {
      removeItemFromSelection(itemId);
    }
  }, [isSelected,
    itemId,
    validateItems,
    isSelectedValid,
    addItemToSelection,
    removeItemFromSelection,
    sourceItem,
    validation]);

  const onChange = useCallback(() => {
    // Since this an onChange method, the initial state of `isSelected` is false.
    // If the item is not selected, we validate it first before adding it to the selection
    // If it is selected, we remove it from the selection.
    // This is to ensure that we do not add invalid items to the selection.
    if (!isSelected) {
      setIsValidating(true);
      validatingItems();
    } else {
      setIsValidating(false);
      removeItemFromSelection(itemId);
    }
  }, [isSelected,
    removeItemFromSelection,
    addItemToSelection,
    itemId]);


  const disabled = !validation.isValid;

  const hasProduct = useMemo(() => !isNull(details));

  const [isIconHovering, setIconHovering] = useState(false);

  return (
    isValidating ? (
      <ItemLoadingSpinner>
        <LoadingSpinner />
      </ItemLoadingSpinner>
    ) :
      <StyledItemSelector
        tabIndex="-1"
        key={`selector-${itemId}`}
        isSelected={isSelected}
        disabled={disabled}
      >
        <ItemSelectorCheckbox
          name={`checkbox-${itemId}`}
          checked={isSelected}
          label={itemName}
          disabled={disabled}
          onChange={onChange}
          itemId={itemId}
          newWindowLabel={newWindowLabel}
        />
        <ItemSelectorIcon
          onMouseEnter={() => setIconHovering(true)}
          onMouseLeave={() => setIconHovering(false)}
        >
          { hasProduct && validation.isValid && <CopyStylesProduct /> }
          { hasProduct && !validation.isValid && <CopyStylesProductInfo /> }
          { !hasProduct && <CopyStylesEmpty /> }
        </ItemSelectorIcon>
        {(validation.isValid
            || (!validation.isValid && isIconHovering))
            && details && <ItemSelectorDetails {...details} />}
        {!validation.isValid
            && !isIconHovering
            && <ItemSelectorValidationErrors errors={validation.errors} />}
      </StyledItemSelector>
  );
};

ItemSelector.propTypes = {
  itemId: PropTypes.string.isRequired,
  itemName: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired,
  addItemToSelection: PropTypes.func.isRequired,
  removeItemFromSelection: PropTypes.func.isRequired,
  newWindowLabel: PropTypes.shape({}).isRequired,
  validateItems: PropTypes.func.isRequired,
  details: PropTypes.shape({
    brand: PropTypes.string,
    productName: PropTypes.string,
    color: PropTypes.string,
  }),
  validation: PropTypes.shape({
    isValid: PropTypes.bool,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  sourceItem: PropTypes.string.isRequired,
  isSelectedValid: PropTypes.bool.isRequired,
};
ItemSelector.defaultProps = {
  details: undefined,
  validation: {
    isValid: true,
  },
};

export default ItemSelector;
